import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootReducerType, AppDispatch, RootState } from "@store/store";
import { postPayloadBkpayThunk } from "@store/thunk/PaymentThunk";
import {
  FinalPaymentThunk,
  FeesDetailsThunk,
  fetchApplicationDetails,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import PaymentLoader from "../../../utils/PaymentLoader";

const FinalLicenseFee: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [seachParams] = useSearchParams();
  const access = seachParams.get("access");
  const row = seachParams.get("row");

  const { access_id } = useSelector(
    (state: RootReducerType) => state.amenities,
  );

  const { applicationDetails } = useSelector(
    (state: RootState) => state.applicationDetails,
  );

  useEffect(() => {
    dispatch(fetchApplicationDetails(access || row || access_id));
  }, [dispatch, access_id]);

  const { profile } = useSelector((state: RootReducerType) => state.profile);
  const [showLoader, setShowLoader] = useState(false);

  // ✅ feesDetails from Redux
  const { feesDetails } = useSelector((state: any) => state.feesDetails);

  // ✅ Fetch feesDetails on load
  useEffect(() => {
    const fetchFeesDetails = async () => {
      if (access_id || access || row) {
        try {
          const feedetailsResp = await dispatch(
            FeesDetailsThunk({ applicationId: access_id || access || row }),
          );
          if(feedetailsResp.payload?.success === false) {
            navigate("/application-dashboard");
            return;
          }
        } catch (error) {
          navigate("/application-dashboard");
          console.error("Error fetching fees details:", error);
        }
      }
    };
    fetchFeesDetails();
  }, [access_id, access, row, dispatch, navigate]);


  const handleSubmit = async () => {
    const applicationId = access ?? row;
    if (applicationId == null) {
      toast.error("Application ID is missing.");
      return;
    }
    try {
      const res = await dispatch(
        FinalPaymentThunk({ applicationId }),
      ).unwrap();
      if (res.payload?.success) {
        toast.success(res.payload?.message || "Payment successful!");
        navigate("/application-dashboard");
      } else {
        toast.error(res.payload?.message || "Payment failed.");
      }
    } catch (error) {
      console.error("Error submitting final payment:", error);
      toast.error("An error occurred while submitting the payment.");
    }
  };

  const handlePostBase64 = async () => {
    try {
      const fullName = profile?.name || "";
      const nameParts = fullName
        ?.trim()
        .split(" ")
        .filter((part) => part.length > 0);
      const firstName = nameParts?.length > 0 ? nameParts?.[0] : "";
      const lastName =
        nameParts?.length > 1 ? nameParts?.slice(1).join(" ") : "";
      let mobileNumber = profile?.mobile_number || "";
      if (mobileNumber.startsWith("+91")) {
        mobileNumber = mobileNumber.slice(3);
      }
      const bkPayload = {
        application_id: access_id || access || row,
        FirstName: firstName || "",
        LastName: lastName || "",
        Address1: profile?.address || "",
        Address2: "",
        // PostalCode: "",
        // City: "",
        // StateRegion: "",
        // State: "",
        PostalCode: profile?.pincode || "",
        City: profile?.district || "",
        StateRegion: profile?.district || "",
        State: profile?.state || "",
        CountryCode: "INDIA",
        MobileNumber: mobileNumber,
        payment_stage: 3,
      };
      const getSignedBase64: any = await dispatch(
        postPayloadBkpayThunk(bkPayload),
      ).unwrap();
      if (getSignedBase64.success === false) {
        toast.error(
          getSignedBase64.message ||
          "Failed to initiate payment. Please try again.",
        );
        setShowLoader(false);
        return;
      }
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        process.env.REACT_APP_BHARATKOSH_URL || "https://bharatkosh.gov.in/";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "bharrkkosh";
      input.value = getSignedBase64;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      setShowLoader(false);
      console.error("Error posting base64:", error);
    }
  };

  const handleFinalPayNow = async () => {
    const appId = access_id || access || row;
    if (!appId) {
      toast.error("Application ID is missing.");
      return;
    }
    setShowLoader(true);
    await handlePostBase64();
  };

  return (
    <>
      <div className="page-content">
        <PaymentLoader open={showLoader} />
        <Box className="app-details-card" sx={{ p: { xs: 0, sm: 2 } }}>
          <Box className="application-banner">
            <Box sx={{ display: "flex", gap: { xs: 1, sm: 3 }, px: 0 }}>
              <Paper sx={{ flex: 1, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    mb: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1, sm: 0 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ArrowBackIcon
                      onClick={() => navigate(`/application-dashboard`)}
                      sx={{
                        mr: { xs: 0, sm: 1 },
                        fontSize: { xs: 18, sm: 22 },
                        color: "#222",
                        cursor: "pointer",
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      gutterBottom
                      sx={{
                        fontSize: { xs: "16px", sm: "20px" },
                        flex: 1,
                        mt: { xs: 1, sm: 0 },
                      }}
                    >
                      Fee Payment
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", mt: { xs: 1, sm: 0 } }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                    >
                      Application No:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                    >
                      {applicationDetails?.application_code}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Alert severity="error" sx={{ mb: 2, fontSize: "14px" }}>
                  <strong>Important:</strong> Please wait for 20 minutes if you
                  have recently attempted the payment. If the amount has been
                  deducted but an Application Status was not updated, contact
                  our support team at{" "}
                  <a
                    href="mailto: support-rmp@gov.in"
                    style={{ textDecoration: "underline" }}
                  >
                    {" "}
                    support-rmp@gov.in
                  </a>{" "}
                  for assistance.
                </Alert>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 2, sm: 3 },
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {/* Further Instructions Section */}
                  <Box
                    sx={{
                      flex: 1,
                      p: { xs: 2, sm: 3 },
                      border: "1px solid #e0e0e0",
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      minWidth: { xs: "auto", sm: 300 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                      color="text.primary"
                      gutterBottom
                    >
                      Futher Instructions:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      paragraph
                    >
                      1. Upon approval of the Application, the Applicant shall
                      be under a legal obligation to furnish a Bank Guarantee in
                      the sum of INR 2,50,000 (Rupees Two Lakh Fifty Thousand
                      only) in favour of the Competent Authority, in such form
                      and manner as prescribed under the applicable rules and
                      guidelines of the Ministry of Road Transport & Highways
                      (MoRTH). The final fee shall be determined after
                      considering the applicable 5% annual increment effective
                      from 26/06/2020, in accordance with the prevailing rules
                      and guidelines.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      paragraph
                    >
                      2. In the event of rejection of the Application, the
                      Applicant shall be required to remit afresh the
                      non-refundable Processing Fee of INR 10,000 (Rupees Ten
                      Thousand only) for every subsequent re-submission of the
                      Application.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      paragraph
                    >
                      3. The consideration, examination, and approval of the
                      Application shall be subject in all respects to, and
                      governed strictly by, the MoRTH Guidelines, Rules, and
                      Norms as may be amended from time to time.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      paragraph
                    >
                      4. The review, scrutiny, and evaluation of the Application
                      shall commence only upon the successful submission
                      thereof, accompanied by confirmation of payment of all
                      applicable fees and charges.
                    </Typography>
                  </Box>

                  {/* Payment Details Section */}
                  <Box
                    sx={{
                      width: { xs: "100%", sm: 360 },
                      p: { xs: 2, sm: 3 },
                      borderRadius: 2,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      mb={2}
                      color="#3A1C73"
                      sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                    >
                      Payment Details
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        <strong>Applicant Name:</strong>{" "}
                        {profile?.name || "Rahul Sharma"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        <strong>Amenity Name:</strong>{" "}
                        {applicationDetails?.application_category || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        <strong>Stretch Reach Type:</strong>{" "}
                        {applicationDetails?.location_type_name || "-"}
                      </Typography>

                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        <strong>Location Address:</strong>{" "}
                        {[
                          applicationDetails?.address_1,
                          applicationDetails?.address_2,
                          applicationDetails?.district,
                          applicationDetails?.state,
                        ]
                          .filter(Boolean) // remove null/undefined/empty
                          .join(", ")}
                      </Typography>
                    </Box>

                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                    >
                      Fee Breakup for Final NOC
                    </Typography>

                    <Box sx={{ borderBottom: "1px dashed #bbb", mb: 2 }} />

                    {/* Application Processing Fee */}
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        Final NOC Fee
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                      >
                        Rs.{" "}
                        {feesDetails?.finalLicenseFees?.toLocaleString(
                          "en-IN",
                        ) || "0.00"}
                      </Typography>
                    </Box>

                    <Box sx={{ borderBottom: "1px dashed #bbb", my: 2 }} />

                    {/* Total calculation */}
                    <Box display="flex" justifyContent="space-between" mb={3}>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      >
                        Total
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      >
                        Rs.{" "}
                        {feesDetails?.finalLicenseFees?.toLocaleString(
                          "en-IN",
                        ) || "0.00"}
                      </Typography>
                    </Box>
                    {feesDetails?.finalLicenseFees === 0 ?
                      <>
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={showLoader}
                          onClick={handleSubmit}
                          sx={{
                            borderRadius: "24px",
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: "#3A1C73",
                            padding: { xs: "10px", sm: "12px" },
                            fontSize: { xs: "14px", sm: "16px" },
                            height: { xs: "36px", sm: "40px" },
                            "&:hover": {
                              backgroundColor: "#2a145c",
                            },
                          }}
                        >
                          Submit Application
                        </Button>
                      </> :
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={showLoader}
                        onClick={handleFinalPayNow}
                        sx={{
                          borderRadius: "24px",
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor: "#3A1C73",
                          padding: { xs: "10px", sm: "12px" },
                          fontSize: { xs: "14px", sm: "16px" },
                          height: { xs: "36px", sm: "40px" },
                          "&:hover": {
                            backgroundColor: "#2a145c",
                          },
                        }}
                      >
                        Pay Now
                      </Button>
                    }
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </div>
    </>
  );
};

export default FinalLicenseFee;
